// material-ui
import { Theme } from '@mui/material/styles';
import { ChipProps } from '@mui/material/Chip';

// project imports
import { withAlpha } from 'utils/colorUtils';

// ===============================||  OVERRIDES - CHIP  ||=============================== //

export default function Chip(theme: Theme) {
  const hasChipPaletteShape = (value: unknown): value is { main: string; light: string; dark: string } => {
    return !!value && typeof value === 'object' && 'main' in value && 'light' in value && 'dark' in value;
  };

  // The brand success greens are too dark to read on the navy dark-mode surfaces, so use the lighter 200 shade there
  const successOnDark = theme.palette.success[200];

  return {
    MuiChip: {
      defaultProps: {
        color: 'primary' as ChipProps['color'],
        variant: 'light' as ChipProps['variant']
      },
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: 'light' }, // Variant for light Chip
              style: ({ ownerState, theme }: { ownerState: ChipProps; theme: Theme }) => {
                // Make sure color exists and is a key of palette
                const colorKey = ownerState.color as keyof typeof theme.palette;
                const paletteColor = theme.palette[colorKey];

                if (!hasChipPaletteShape(paletteColor)) return {};

                return {
                  color: paletteColor.main,
                  backgroundColor: paletteColor.light,

                  ...(ownerState.color === 'error' && {
                    backgroundColor: withAlpha(paletteColor.light, 0.25)
                  }),
                  ...(ownerState.color === 'success' && {
                    backgroundColor: withAlpha(paletteColor.light, 0.5)
                  }),
                  ...((ownerState.color === 'warning' || ownerState.color === 'success') && {
                    color: paletteColor.dark
                  }),

                  // Dark mode styles
                  ...theme.applyStyles('dark', {
                    backgroundColor: withAlpha(paletteColor.dark, 0.15),
                    ...(ownerState.color === 'error' && {
                      backgroundColor: withAlpha(paletteColor.dark, 0.15)
                    }),
                    ...(ownerState.color === 'success' && {
                      backgroundColor: withAlpha(paletteColor.main, 0.25),
                      color: successOnDark
                    }),
                    ...(ownerState.color === 'warning' && {
                      color: paletteColor.main
                    })
                  }),

                  '&.MuiChip-clickable': {
                    '&:hover': {
                      color: paletteColor.light,
                      backgroundColor: paletteColor.dark,

                      ...theme.applyStyles('dark', {
                        color: paletteColor.dark,
                        backgroundColor: paletteColor.light
                      })
                    }
                  }
                };
              }
            },
            {
              props: { variant: 'outlined', color: 'warning' },
              style: {
                borderColor: theme.palette.warning.dark,
                color: theme.palette.warning.dark,
                ...theme.applyStyles('dark', {
                  borderColor: theme.palette.warning.main,
                  color: theme.palette.warning.main,
                  backgroundColor: withAlpha(theme.palette.warning.main, 0.12)
                })
              }
            },
            {
              props: { variant: 'outlined', color: 'success' },
              style: {
                borderColor: theme.palette.success.dark,
                color: theme.palette.success.dark,
                ...theme.applyStyles('dark', {
                  borderColor: successOnDark,
                  color: successOnDark,
                  backgroundColor: withAlpha(theme.palette.success.main, 0.25)
                })
              }
            }
          ],
          '&.MuiChip-deletable .MuiChip-deleteIcon': {
            color: 'inherit'
          }
        }
      }
    }
  };
}
